import { Injectable } from '@angular/core';
import { BASE_API_URL, IBoondockRating, IGuestRating, IListingRating, IPoiRating, IReview, ISubPhoto } from '@curbnturf/entities';
import { CachedHttpClient } from '@curbnturf/network';
import { Observable } from 'rxjs';
import { RatingActionTypes } from './+state/rating.actions';

const API_URL = BASE_API_URL + 'rating';

@Injectable({
  providedIn: 'root',
})
export class RatingService {
  constructor(private http: CachedHttpClient) {}

  fetchListingRatings(listingId: number): Observable<IListingRating[]> {
    if (!listingId) {
      throw new Error('Unable to fetch ratings. No ID provided');
    }

    return this.http.get<IListingRating[]>(`${API_URL}/listing/${listingId}`);
  }

  fetchGuestRatings(userId: number): Observable<IGuestRating[]> {
    if (!userId) {
      throw new Error('Unable to fetch guest ratings. No ID provided');
    }

    return this.http.get<IGuestRating[]>(`${API_URL}/guest/${userId}`);
  }

  fetchBoondockRatings(boondockId: number): Observable<IBoondockRating[]> {
    if (!boondockId) {
      throw new Error('Unable to fetch boondock ratings. No ID provided');
    }

    return this.http.get<IBoondockRating[]>(`${API_URL}/boondock/${boondockId}`);
  }

  fetchPoiRatings(poiId: number): Observable<IPoiRating[]> {
    if (!poiId) {
      throw new Error('Unable to fetch GOI ratings. No ID provided');
    }

    return this.http.get<IPoiRating[]>(`${API_URL}/poi/${poiId}`);
  }

  createListingRatings(ratings: IListingRating[]): Observable<IListingRating[]> {
    const saveableRatings = ratings.map((rating) => ({
      ...rating,
      creatorId: rating.creator?.id || rating.creatorId,
      creator: {
        id: rating.creator?.id || rating.creatorId,
      },
    }));
    return this.http.post<IListingRating[]>(`${API_URL}/listing`, saveableRatings, undefined, {
      offlineCallback: () => saveableRatings,
      syncAction: { type: RatingActionTypes.SyncListingRatings },
    });
  }

  reviewListing(listingId: number, review: IReview): Observable<IReview> {
    return this.http.post<IReview>(`${API_URL}/listing/${listingId}/review`, review);
  }

  reviewGuest(guestId: number, review: IReview): Observable<IReview> {
    return this.http.post<IReview>(`${API_URL}/guest/${guestId}/review`, review);
  }

  uploadReviewPhoto(listingId: number, reviewId: number, formData: FormData): Observable<ISubPhoto[]> {
    return this.http.post<ISubPhoto[]>(`${API_URL}/listing/${listingId}/review/${reviewId}/photo`, formData);
  }

  createGuestRatings(ratings: IGuestRating[]): Observable<IGuestRating[]> {
    const saveableRatings = ratings.map((rating) => ({
      ...rating,
      creatorId: rating.creator?.id || rating.creatorId,
      creator: {
        id: rating.creator?.id || rating.creatorId,
      },
    }));
    return this.http.post<IGuestRating[]>(`${API_URL}/guest`, saveableRatings, undefined, {
      offlineCallback: () => saveableRatings,
      syncAction: { type: RatingActionTypes.SyncGuestRatings },
    });
  }

  createBoondockRatings(ratings: IBoondockRating[]): Observable<IBoondockRating[]> {
    return this.http.post<IBoondockRating[]>(
      `${API_URL}/boondock`,
      ratings.map((rating) => ({
        ...rating,
        creatorId: rating.creator?.id || rating.creatorId,
        ratedId: rating.rated?.id || rating?.ratedId,
        creator: {
          id: rating.creator?.id || rating.creatorId,
        },
      })),
    );
  }

  createPoiRatings(ratings: IPoiRating[]): Observable<IPoiRating[]> {
    return this.http.post<IPoiRating[]>(
      `${API_URL}/poi`,
      ratings.map((rating) => ({
        ...rating,
        creatorId: rating.creator?.id,
        poiId: rating.poiRated?.id,
      })),
    );
  }
}
